import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { toast } from "sonner";
import {
  ArrowLeft,
  Loader2,
  UserCircle,
  Phone,
  Mail,
  Calendar,
  MapPin,
  ShoppingCart,
  Package,
  IndianRupee,
  Eye,
  ShieldOff,
  ShieldCheck,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import axios from "axios";

const API = "/api";
const BACKEND_URL = "";

const statusColors = {
  pending: "bg-yellow-100 text-yellow-700",
  confirmed: "bg-blue-100 text-blue-700",
  processing: "bg-purple-100 text-purple-700",
  out_for_delivery: "bg-orange-100 text-orange-700",
  delivered: "bg-green-100 text-green-700",
  cancelled: "bg-red-100 text-red-700",
};

const statusLabels = {
  pending: "Pending",
  confirmed: "Confirmed",
  processing: "Processing",
  out_for_delivery: "Out for Delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};

const UserDetailsPage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [blocking, setBlocking] = useState(false);

  const getAuthHeaders = () => ({
    headers: { Authorization: `Bearer ${localStorage.getItem("ecomdemo_token")}` },
  });

  const fetchUser = async () => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`, getAuthHeaders());
      setData(res.data);
    } catch {
      toast.error("Failed to load user details");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchUser(); }, [userId]);

  const handleToggleBlock = async () => {
    setBlocking(true);
    try {
      const res = await axios.put(`${API}/admin/users/${userId}/block`, {}, getAuthHeaders());
      setData((prev) => ({ ...prev, user: { ...prev.user, is_blocked: res.data.is_blocked } }));
      toast.success(res.data.message);
    } catch {
      toast.error("Failed to update user status");
    } finally {
      setBlocking(false);
    }
  };

  const formatDate = (ds) =>
    new Date(ds).toLocaleDateString("en-IN", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });

  const formatPhone = (p) =>
    p && p.startsWith("91") && p.length === 12
      ? `+91 ${p.slice(2, 7)} ${p.slice(7)}`
      : p;

  const getImageUrl = (path) => {
    if (!path) return null;
    if (path.startsWith("http")) return path;
    if (path.startsWith("/uploads/")) return `${BACKEND_URL}/api${path}`;
    return `${BACKEND_URL}${path}`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="animate-spin text-[#6C42F5]" size={36} />
      </div>
    );
  }

  if (!data) {
    return (
      <div className="text-center py-24">
        <UserCircle className="mx-auto text-gray-300 mb-4" size={48} />
        <p className="text-gray-500">User not found</p>
        <Button variant="outline" className="mt-4" onClick={() => navigate("/admin/users")}>
          Back to Users
        </Button>
      </div>
    );
  }

  const { user, addresses, orders, stats } = data;

  return (
    <div data-testid="user-details-page">
      {/* Back button */}
      <Button
        variant="ghost"
        onClick={() => navigate("/admin/users")}
        className="mb-6 text-gray-600 hover:text-gray-900 -ml-2"
        data-testid="back-to-users-btn"
      >
        <ArrowLeft size={18} className="mr-2" />
        Back to Users
      </Button>

      {/* User profile card */}
      <div className="bg-white border border-gray-200 rounded-xl p-6 mb-6 shadow-sm" data-testid="user-profile-card">
        <div className="flex flex-col sm:flex-row sm:items-center gap-5">
          <div className={`w-16 h-16 rounded-full flex items-center justify-center shrink-0 ${user.is_blocked ? "bg-red-100" : "bg-[#6C42F5]/10"}`}>
            <UserCircle className={user.is_blocked ? "text-red-400" : "text-[#6C42F5]"} size={40} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="text-2xl font-bold text-gray-900">{user.name}</h1>
              {user.is_blocked ? (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-600">
                  <ShieldOff size={12} /> Blocked
                </span>
              ) : (
                <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-600">
                  <ShieldCheck size={12} /> Active
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-x-6 gap-y-2 mt-2 text-sm text-gray-600">
              <span className="flex items-center gap-1.5">
                <Phone size={14} /> {formatPhone(user.phone)}
              </span>
              <span className="flex items-center gap-1.5">
                <Mail size={14} /> {user.email}
              </span>
              <span className="flex items-center gap-1.5">
                <Calendar size={14} /> Joined {formatDate(user.created_at)}
              </span>
            </div>
          </div>
          <Button
            onClick={handleToggleBlock}
            disabled={blocking}
            variant="outline"
            className={user.is_blocked
              ? "border-green-300 text-green-600 hover:bg-green-50 shrink-0"
              : "border-red-300 text-red-500 hover:bg-red-50 shrink-0"}
          >
            {blocking ? (
              <Loader2 size={15} className="animate-spin mr-2" />
            ) : user.is_blocked ? (
              <ShieldCheck size={15} className="mr-2" />
            ) : (
              <ShieldOff size={15} className="mr-2" />
            )}
            {user.is_blocked ? "Unblock User" : "Block User"}
          </Button>
        </div>

        {/* Stats row */}
        <div className="grid grid-cols-3 gap-4 mt-6 pt-6 border-t border-gray-100">
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total_orders}</p>
            <p className="text-xs text-gray-500 mt-0.5">Orders</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">
              {stats.total_spent.toLocaleString("en-IN", { style: "currency", currency: "INR", maximumFractionDigits: 0 })}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">Total Spent</p>
          </div>
          <div className="text-center">
            <p className="text-2xl font-bold text-gray-900">{stats.total_addresses}</p>
            <p className="text-xs text-gray-500 mt-0.5">Addresses</p>
          </div>
        </div>
      </div>

      {/* Addresses */}
      <div className="mb-6">
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <MapPin size={20} className="text-[#6C42F5]" />
          Saved Addresses ({addresses.length})
        </h2>
        {addresses.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl p-8 text-center shadow-sm">
            <MapPin className="mx-auto text-gray-300 mb-2" size={32} />
            <p className="text-gray-500 text-sm">No saved addresses</p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2">
            {addresses.map((addr) => (
              <div
                key={addr.id}
                className="bg-white border border-gray-200 rounded-xl p-4 shadow-sm"
                data-testid={`address-card-${addr.id}`}
              >
                <p className="font-medium text-gray-900 mb-1">{addr.address_name}</p>
                <p className="text-sm text-gray-600">{addr.address_line_1}</p>
                {addr.address_line_2 && (
                  <p className="text-sm text-gray-600">{addr.address_line_2}</p>
                )}
                <p className="text-sm text-gray-600">
                  {addr.city}, {addr.state} - {addr.pincode}
                </p>
                <p className="text-sm text-gray-600">{addr.country}</p>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Order History */}
      <div>
        <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2 mb-4">
          <ShoppingCart size={20} className="text-[#6C42F5]" />
          Order History ({orders.length})
        </h2>

        <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
          {orders.length === 0 ? (
            <div className="p-8 text-center">
              <ShoppingCart className="mx-auto text-gray-300 mb-2" size={32} />
              <p className="text-gray-500 text-sm">No orders placed yet</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-gray-50">
                    <TableHead className="font-bold text-gray-700">Order ID</TableHead>
                    <TableHead className="font-bold text-gray-700">Items</TableHead>
                    <TableHead className="font-bold text-gray-700">Total</TableHead>
                    <TableHead className="font-bold text-gray-700">Payment</TableHead>
                    <TableHead className="font-bold text-gray-700">Status</TableHead>
                    <TableHead className="font-bold text-gray-700">Date</TableHead>
                    <TableHead className="font-bold text-gray-700 text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {orders.map((order) => (
                    <TableRow key={order.id} className="hover:bg-gray-50" data-testid={`order-row-${order.id}`}>
                      <TableCell className="font-mono text-sm text-gray-600">
                        {order.id.slice(0, 8)}...
                      </TableCell>
                      <TableCell className="text-gray-600">{order.total_items} items</TableCell>
                      <TableCell className="font-medium text-gray-900">
                        <span className="flex items-center gap-0.5">
                          <IndianRupee size={13} />
                          {order.total_amount.toFixed(2)}
                        </span>
                      </TableCell>
                      <TableCell className="text-gray-600">{order.payment_method}</TableCell>
                      <TableCell>
                        <Badge className={statusColors[order.status] || "bg-gray-100 text-gray-700"}>
                          {statusLabels[order.status] || order.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-gray-500 text-sm">{formatDate(order.created_at)}</TableCell>
                      <TableCell className="text-right">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setSelectedOrder(order)}
                          className="text-gray-600 hover:text-[#6C42F5]"
                          data-testid={`view-order-btn-${order.id}`}
                        >
                          <Eye size={16} />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </div>

      {/* Order detail dialog */}
      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="bg-white sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-gray-900">Order Details</DialogTitle>
          </DialogHeader>
          {selectedOrder && (
            <div className="space-y-5 mt-4">
              <div className="flex flex-wrap justify-between gap-4">
                <div>
                  <p className="text-sm text-gray-500">Order ID</p>
                  <p className="font-mono text-sm">{selectedOrder.id}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Date</p>
                  <p className="text-sm">{formatDate(selectedOrder.created_at)}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Status</p>
                  <Badge className={statusColors[selectedOrder.status]}>
                    {statusLabels[selectedOrder.status] || selectedOrder.status}
                  </Badge>
                </div>
              </div>

              {/* Delivery address */}
              {selectedOrder.delivery_address && (
                <div className="bg-gray-50 rounded-lg p-4">
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <MapPin size={16} /> Delivery Address
                  </h3>
                  <div className="text-sm text-gray-600">
                    {selectedOrder.delivery_address.address_name && (
                      <p className="font-medium text-gray-900">{selectedOrder.delivery_address.address_name}</p>
                    )}
                    <p>{selectedOrder.delivery_address.address_line_1}</p>
                    {selectedOrder.delivery_address.address_line_2 && (
                      <p>{selectedOrder.delivery_address.address_line_2}</p>
                    )}
                    {selectedOrder.delivery_address.city && (
                      <p>
                        {selectedOrder.delivery_address.city}, {selectedOrder.delivery_address.state} -{" "}
                        {selectedOrder.delivery_address.pincode}
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* Items */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
                  <Package size={16} /> Items
                </h3>
                <div className="space-y-3">
                  {selectedOrder.items.map((item, i) => (
                    <div key={i} className="flex items-center gap-4 bg-gray-50 rounded-lg p-3">
                      {item.product_image ? (
                        <img
                          src={getImageUrl(item.product_image)}
                          alt={item.product_name}
                          className="w-12 h-12 rounded-lg object-cover"
                        />
                      ) : (
                        <div className="w-12 h-12 rounded-lg bg-gray-200 flex items-center justify-center">
                          <Package className="text-gray-400" size={18} />
                        </div>
                      )}
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{item.product_name}</p>
                        <p className="text-sm text-gray-500">
                          {"\u20B9"}{item.product_price?.toFixed(2)} x {item.quantity}
                        </p>
                      </div>
                      <p className="font-semibold text-gray-900">{"\u20B9"}{item.subtotal?.toFixed(2)}</p>
                    </div>
                  ))}
                </div>
              </div>

              {/* Summary */}
              <div className="border-t pt-4 flex justify-between font-bold text-lg text-gray-900">
                <span>Total</span>
                <span>{"\u20B9"}{selectedOrder.total_amount.toFixed(2)}</span>
              </div>

              <div className="border-t pt-3 text-sm text-gray-600">
                <span className="font-medium">Payment:</span> {selectedOrder.payment_method}
                {selectedOrder.notes && (
                  <p className="mt-1"><span className="font-medium">Notes:</span> {selectedOrder.notes}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UserDetailsPage;
